import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { auth } from "byzantine";
import { useLocalization } from "@fluent/react";
import { Button, Checkbox, TextInput, Tooltip } from "@narmi/design_system";
import {
  ContextForm,
  useNotificationContext,
  useFormData,
  CsrfTokenMiddleware,
  jsonFromDocument
} from "cerulean";
import Feature from "byzantine/src/Feature";
import type { InstitutionFeatures } from "byzantine/src/types";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import type Institution from "../../types/institution";
import { createHash } from "./utils";
import "./authentication.css";

const { getMfaDigits } = auth;

type MfaDevice = {
  id: string;
  name: string;
};

type MfaCodeEnterProps = {
  challengeText?: string;
  deviceSelected: MfaDevice;
  loginLinkFlow?: string;
  features: InstitutionFeatures;
  institution: Institution;
};

const deviceMonitoringModes = ["report", "decision"];

const MfaCodeEnter = ({
  challengeText = "",
  deviceSelected,
  loginLinkFlow,
  features,
  institution
}: MfaCodeEnterProps) => {
  const [debugTimeToSubmit, setDebugTimeToSubmit] = useState(3);
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const loginLinkFlowBoolean = loginLinkFlow === "True";
  const form = useRef<HTMLFormElement>(null);

  const deviceIdInput = useRef<HTMLInputElement>(null);
  const deviceRequestIdInput = useRef<HTMLInputElement>(null);
  const { getData } = useVisitorData(
    { extendedResult: true, tag: { userAction: "mfa_enter" } },
    { immediate: true }
  );

  const resendCode = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const otpTokenInput = form.current?.elements.namedItem(
      "otp_token"
    ) as HTMLInputElement | null;
    if (otpTokenInput) {
      otpTokenInput.value = "";
    }
    form.current?.submit();
  };

  useEffect(() => {
    const formErrors = jsonFromDocument("form_non_field_errors", "[]");
    if (formErrors.length) {
      const error = formErrors[0];
      if (error !== "Please enter your OTP token.") {
        sendNotification({ type: "negative", text: formErrors[0] });
      }
    }
  }, [sendNotification]);

  const username = jsonFromDocument("username", "");
  const usernameHash = useCallback(
    async (message: string) => createHash(message),
    []
  );

  const isBackupCode = deviceSelected.name === "Backup codes";
  const isApp = /^App added/.test(deviceSelected.name);
  let challengeTextDisplay = challengeText;
  if (isBackupCode) {
    challengeTextDisplay = l10n.getString(
      "prompt-enter-backup-code",
      null,
      "Enter one of your backup codes."
    );
  } else if (isApp) {
    challengeTextDisplay = l10n.getString(
      "prompt-use-authenticator",
      null,
      "Use an authenticator app to get a code."
    );
  }

  const otpToken = useMemo(() => {
    if (import.meta.env.PROD) return "";
    return getMfaDigits(challengeTextDisplay);
  }, [challengeTextDisplay]);

  const { formData, onChange } = useFormData({
    otp_token: otpToken,
    remember_this_device: loginLinkFlowBoolean
  });

  // Login after 3 seconds if otpToken exists and not in production environment
  useEffect(() => {
    if (!otpToken) return undefined;

    const id = window.setInterval(() => {
      setDebugTimeToSubmit((prev) => {
        if (prev <= 1) {
          window.clearInterval(id);
          form.current?.submit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(id);
  }, [otpToken]);

  const queryParams = new URLSearchParams(window.location.search);
  const next = queryParams.get("next");
  const redirectUrl = next ? encodeURIComponent(next) : "/";
  const shouldCollectFingerprint =
    Boolean(institution.fingerprintApiKey) &&
    deviceMonitoringModes.includes(
      features.third_party_login_evaluations ?? "off"
    );

  return (
    <div className="authentication-container">
      <ContextForm
        ref={form}
        data={formData}
        onChange={onChange}
        action={`/login/verify_code_enter?next=${redirectUrl}`}
        autoComplete="off"
        method="post"
      >
        <h1>{"Let's verify your account"}</h1>
        <div data-testid="mfa-challenge-text">{challengeTextDisplay}</div>
        {!isBackupCode && !isApp && (
          <div className="resend-code-container">
            <Button
              label={l10n.getString("button-resend-code", null, "Resend code")}
              testId="resend-code"
              type="button"
              kind="plain"
              size="s"
              onClick={resendCode}
            />
            {otpToken && (
              <div style={{ paddingTop: "12px" }}>
                Submitting in {debugTimeToSubmit}
              </div>
            )}
          </div>
        )}
        <div className="code-input-container">
          <ContextForm.Field required>
            <TextInput
              field="otp_token"
              name="otp_token"
              label={l10n.getString("label-enter-code", null, "Enter code")}
              type={isBackupCode ? "text" : "tel"}
              data-testid="enter-code-input"
            />
          </ContextForm.Field>
          {shouldCollectFingerprint && (
            <ContextForm.Field>
              <input
                type="hidden"
                id="device_id"
                name="device_id"
                ref={deviceIdInput}
              />
            </ContextForm.Field>
          )}
          {shouldCollectFingerprint && (
            <ContextForm.Field>
              <input
                type="hidden"
                id="device_request_id"
                name="device_request_id"
                ref={deviceRequestIdInput}
              />
            </ContextForm.Field>
          )}
        </div>
        <Feature features={features} or={["allow_olb_device_remembering"]}>
          <div className="margin--top--s">
            <ContextForm.Field style={{ display: "inline-flex", marginTop: 0 }}>
              <div className="margin--right--xxs">
                <Checkbox
                  id="remember_this_device"
                  name="remember_this_device"
                  {...{ field: "remember_this_device" }}
                  label={l10n.getString(
                    "label-remember-device",
                    null,
                    "Remember this device"
                  )}
                />
              </div>
              <Tooltip
                text={l10n.getString(
                  "tooltip-shared-device",
                  null,
                  "Do not select this box if you're using a public or shared device."
                )}
              >
                <span className="narmi-icon-info fontColor--primary fontSize--s padding--top--xxs" />
              </Tooltip>
            </ContextForm.Field>
          </div>
        </Feature>
        <input name="otp_device" hidden readOnly value={deviceSelected.id} />
        <CsrfTokenMiddleware />
        <ContextForm.ActionBar>
          <ContextForm.Action
            onSubmit={(_data: unknown, e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              if (shouldCollectFingerprint) {
                usernameHash(`${institution.internalName}-${username}`).then(
                  (hashedUsername) => {
                    getData({ ignoreCache: true, linkedId: hashedUsername })
                      .then((data) => {
                        if (deviceIdInput.current) {
                          deviceIdInput.current.value = data.visitorId;
                        }
                        if (deviceRequestIdInput.current) {
                          deviceRequestIdInput.current.value = data.requestId;
                        }
                        form.current?.submit();
                      })
                      .catch((error) => {
                        console.warn("Fingerprint collection failed:", error);
                        form.current?.submit();
                      });
                  }
                );
              } else {
                form.current?.submit();
              }
            }}
          >
            <Button
              label={l10n.getString("button-continue", null, "Continue")}
              testId="mfa-code-submit"
            />
          </ContextForm.Action>
        </ContextForm.ActionBar>
      </ContextForm>
    </div>
  );
};

export default MfaCodeEnter;
